import React from "react";
// NAVIGATION
import { useNavigate, useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Dropdown, Space, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faTrash,
  faPenToSquare,
  faBan,
  faElevator,
  faArrowUp19,
  faFileExport,
  faGear,
  faEthernet,
  faCode,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateOpenModalDeleteBank, updateBank } from "../../../redux/banks/bankSlice";
import { updateOpenDrawerAssignInterfaceBank } from "../../../redux/keys/keysSlice";
import { updateBreadcrumbTitle } from "../../../redux/home/homeSlice";
// WIZARD NAVIGATION
import { WizardStep } from "../../setupWizard/utils/wizardInvalidation";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
// INTERFACES
import { BanksTableActionInterface } from "../../../interfaces/Bank.interface";
// INTERFACES
import { RootState } from "../../../app/store";
// AUTH
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { formatUuidT } from "../../../flatbuffers/display";
import downloadEntityAsBinary from "../../../utils/exportEntity";

function BanksTableAction({ selectedItem }: BanksTableActionInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const idServer = new URLSearchParams(location.search).get("idServer");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleOpenModalEdit = () => {
    const orgId = formatUuidT(selectedItem.orgId) || "";
    if (orgId) {
      navigate(`/organization-management/setup-wizard?orgId=${orgId}&step=${WizardStep.Banks}`);
    }
  };

  const handleOpenModalDelete = () => {
    dispatch(updateBank(selectedItem));
    dispatch(updateOpenModalDeleteBank(true));
  };

  const handleOpenDrawerFloorNames = () => {
    localStorage.setItem("headerTitle", "floorsConfiguration");
    dispatch(updateBreadcrumbTitle("floorsConfiguration"));
    navigate({
      pathname: "floors-configuration",
      search: `?idBank=${formatUuidT(selectedItem.id)}${
        idServer && idServer !== "" ? `&idServer=${idServer}` : ""
      }`,
    });
  };

  const handleOpenDrawerLiftsPosition = () => {
    localStorage.setItem("headerTitle", "liftsPositions");
    dispatch(updateBreadcrumbTitle("liftsPositions"));
    navigate({
      pathname: "lifts-positions",
      search: `?idBank=${formatUuidT(selectedItem.id)}`,
    });
  };

  const handleOpenDrawerAssignActions = () => {
    localStorage.setItem("headerTitle", "control-actions");
    dispatch(updateBreadcrumbTitle("control-actions"));
    navigate({
      pathname: "control-actions",
      search: `?idBank=${formatUuidT(selectedItem.id)}`,
    });
  };

  const handleOpenKeysAdapter = () => {
    navigate({
      pathname: "/edge-management/bank/keys",
      search: `?idBank=${formatUuidT(selectedItem.id)}`,
    });
  };

  const handleOpenAssignInterface = () => {
    dispatch(updateBank(selectedItem));
    dispatch(updateOpenDrawerAssignInterfaceBank(true));
  };

  const handleExportBankBinary = async () => {
    if (!globalOrganizationId) {
      message.error("No organization selected.");
      return;
    }
    try {
      await downloadEntityAsBinary(globalOrganizationId, "banks", String(selectedItem.name ?? ""));
    } catch {
      message.error("Failed to export bank.");
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const BLANK = {
      label: (
        <div className="generalStyles__tableActionBox">
          <span>
            <FontAwesomeIcon className="generalStyles__error generalStyles__mrFix" icon={faBan} />
            {t("general.noActions")}
          </span>
        </div>
      ),
      key: "0",
    };

    const FLOOR_NAMES =
      hasPermission(Permission.BanksUpdate, permissionSet ?? []) &&
      selectedItem &&
      selectedItem.type !== undefined &&
      selectedItem.type === LiftType.ELEVATOR
        ? {
            label: (
              <TableActionsMenuItem
                icon={faElevator}
                text={t("edgeManagement.banks.floorNames")}
                onClick={handleOpenDrawerFloorNames}
              />
            ),
            key: "1",
          }
        : null;

    const EDIT = hasPermission(Permission.BanksUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faPenToSquare}
              text={t("organizationManagement.edit")}
              onClick={handleOpenModalEdit}
            />
          ),
          key: "2",
        }
      : null;

    const DELETE = hasPermission(Permission.BanksDelete, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faTrash}
              text={t("organizationManagement.delete")}
              onClick={handleOpenModalDelete}
            />
          ),
          key: "3",
        }
      : null;

    const LIFT_POSITION = hasPermission(Permission.BanksUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faArrowUp19}
              text={t("edgeManagement.banks.liftPosition")}
              onClick={handleOpenDrawerLiftsPosition}
            />
          ),
          key: "4",
        }
      : null;

    const ASSIGN_ACTIONS = hasPermission(Permission.AdaptersUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faGear}
              text={t("adapter.assignActions")}
              onClick={handleOpenDrawerAssignActions}
            />
          ),
          key: "5",
        }
      : null;

    const KEYS = hasPermission(Permission.AdaptersUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faEthernet}
              text={t("general.keysAssign")}
              onClick={handleOpenKeysAdapter}
            />
          ),
          key: "6",
        }
      : null;

    const INTERFACE = hasPermission(Permission.AdaptersUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faCode}
              text={t("general.interfaceAssign")}
              onClick={handleOpenAssignInterface}
            />
          ),
          key: "7",
        }
      : null;

    const EXPORT = hasPermission(Permission.BanksView, permissionSet ?? [])
      ? {
          label: (
            <span>
              <FontAwesomeIcon
                className="generalStyles__info generalStyles__mrFix"
                icon={faFileExport}
              />
              {t("organizationManagement.exportDescription")}
            </span>
          ),
          key: "8",
          onClick: handleExportBankBinary,
        }
      : null;

    // RETURN OBJECT WITH VALID ACTIONS
    if (EDIT || DELETE || FLOOR_NAMES || LIFT_POSITION || ASSIGN_ACTIONS || KEYS || INTERFACE) {
      return [EDIT, ASSIGN_ACTIONS, FLOOR_NAMES, LIFT_POSITION, KEYS, INTERFACE, EXPORT, DELETE];
    }
    return [BLANK];
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Dropdown menu={{ items: manageActions() }} trigger={["click"]}>
        <Space className="generalStyles__hoverCursor">
          <FontAwesomeIcon icon={faEllipsis} />
        </Space>
      </Dropdown>
    </div>
  );
}

export default BanksTableAction;
